import { DropInUIThemeOverrides } from '@shared/components/page-layout/drawers/jpmorgan-drop-in-ui.types';

// Fallbacks mirror the app's default (indigo/slate) palette, used only if a Fuse CSS variable is missing.
const FALLBACK = {
    primary: '#4f46e5',
    card: '#ffffff',
    textDefault: '#1e293b',
    textSecondary: '#64748b',
    textHint: '#94a3b8'
};

// Fixed to match the app's Material fill form fields (border-gray-300 / dark:border-gray-500), and the red used for invalid fields.
const BORDER = { light: '#cbd5e1', dark: '#64748b' };
const DIVIDER = { light: '#e2e8f0', dark: '#334155' };
const CRITICAL = '#dc2626';

const rgbTripleToHex = (triple: string): string | null => {
    const channels = triple.split(',').map(channel => Number(channel.trim()));
    if (channels.length < 3 || channels.some(channel => Number.isNaN(channel))) {
        return null;
    }
    return '#' + channels.slice(0, 3).map(channel => channel.toString(16).padStart(2, '0')).join('');
};

/**
 * Builds the Drop-in UI theme from the app's live styling so the widget follows the active
 * tenant theme (theme-* class) and light/dark scheme instead of one hard-coded palette.
 * Must run once the mount element is in the DOM, since the Fuse variables are resolved from it.
 */
export function buildDropInThemeOverrides(mountElementId: string): DropInUIThemeOverrides {
    const host = document.getElementById(mountElementId) ?? document.body;
    const style = getComputedStyle(host);
    const isDark = host.closest('.dark, .light')?.classList.contains('dark') ?? false;
    const scheme = isDark ? 'dark' : 'light';

    const fuseColor = (token: string, fallback: string): string =>
        rgbTripleToHex(style.getPropertyValue(`--fuse-${token}-rgb`)) ?? fallback;

    const primary = fuseColor('primary', FALLBACK.primary);
    const card = fuseColor('bg-card', FALLBACK.card);

    return {
        color: {
            brand: primary,
            interactive: primary,
            pageBackground: card,
            containerBackground: card,
            formBackground: card,
            primaryText: fuseColor('text-default', FALLBACK.textDefault),
            secondaryText: fuseColor('text-secondary', FALLBACK.textSecondary),
            tertiaryText: fuseColor('text-hint', FALLBACK.textHint),
            border: BORDER[scheme],
            divider: DIVIDER[scheme],
            critical: CRITICAL
        },
        typography: {
            fontFamily: style.fontFamily,
            fontSize: '14px'
        },
        // Tailwind scale used by the app (text-3xl / 2xl / lg / base / sm); the app's base text is 14px.
        fontSizes: { h1: '24px', h2: '20px', h3: '16px', h4: '14px', body: '14px', caption: '12px', button: '14px' },
        borderRadius: { form: '6px', button: '6px' }
    };
}
